import { useEffect, useRef, useState, useCallback } from "react";
import environment from "../config/environment";
import whatsappApiService from "../services/whatsappApiService";

const DEFAULT_STATS = {
  totalMessages: 0,
  unreadMessages: 0,
  conversations: 0,
  unreadConversations: 0,
};

/**
 * Subscribe to WhatsApp inbox SSE stream (server polls DB, pushes new messages).
 */
export function useWhatsAppInboxSSE({ enabled = true, onInboxMessage, onStats } = {}) {
  const sinceIdRef = useRef(0);
  const sourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const onInboxMessageRef = useRef(onInboxMessage);
  const onStatsRef = useRef(onStats);
  const [connectionState, setConnectionState] = useState("idle");
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [lastInboxEvent, setLastInboxEvent] = useState(null);

  onInboxMessageRef.current = onInboxMessage;
  onStatsRef.current = onStats;

  const applyStats = useCallback((nextStats) => {
    if (!nextStats) return;
    setStats(nextStats);
    onStatsRef.current?.(nextStats);
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !environment.ENABLE_NOTIFICATIONS) {
      return undefined;
    }

    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }

    const url = whatsappApiService.getInboxEventsUrl(sinceIdRef.current);
    setConnectionState("connecting");

    const source = new EventSource(url);
    sourceRef.current = source;

    source.addEventListener("connected", () => {
      setConnectionState("open");
    });

    source.addEventListener("inbox_message", (event) => {
      try {
        const payload = JSON.parse(event.data);
        const message = payload?.message;
        if (message?.id) {
          sinceIdRef.current = Math.max(sinceIdRef.current, Number(message.id));
        }
        if (payload?.stats) {
          applyStats(payload.stats);
        }
        setLastInboxEvent(message);
        onInboxMessageRef.current?.(message, payload?.stats);
      } catch {
        // ignore malformed payloads
      }
    });

    source.addEventListener("inbox_stats", (event) => {
      try {
        applyStats(JSON.parse(event.data));
      } catch {
        // ignore
      }
    });

    source.addEventListener("reconnect", () => {
      source.close();
      sourceRef.current = null;
      reconnectTimerRef.current = setTimeout(connect, 300);
    });

    source.onerror = () => {
      setConnectionState("error");
      source.close();
      sourceRef.current = null;
      reconnectTimerRef.current = setTimeout(connect, 4000);
    };

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      source.close();
      sourceRef.current = null;
    };
  }, [enabled, applyStats]);

  useEffect(() => {
    const cleanup = connect();
    return () => {
      if (cleanup) cleanup();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [connect]);

  return {
    connectionState,
    stats,
    lastInboxEvent,
    sinceId: sinceIdRef.current,
  };
}

export default useWhatsAppInboxSSE;
